/**
 * Exercise Section - the slice exercise, authored (MUS-311).
 *
 * The one Phase 1 exercise: scale performance from notation (BL-020, SP-03;
 * DEC-A). This file is the source of truth for the authored content; the
 * committed JSON dump is emitted from it (see emitDumps.ts) and is what
 * MUS-314's seed service ingests.
 *
 * Authoring decisions this data carries (rationale in
 * documentation/exercise-section/05-content-authoring.md):
 * - Unit: one scale/mode holding its twelve tonic-cells in circle-of-fifths
 *   order (no subgroup ladder and no separate review level; the in-unit
 *   full-twelve gauntlet is the review's successor).
 * - Tiers: one waypoint triple, identical across levels (waypoints are
 *   templated, decision #117.5). Beginner sits at hands-together deliberately:
 *   a tier is a single point in frontier space, and "hands separate" is two
 *   points, not one - so B is HT, slow and small, and every star is one
 *   honest dominance check. Each waypoint also names the order a gauntlet over
 *   the level serves its twelve cells in: fixed, fixed, then shuffled at
 *   Advanced.
 * - Provenance: scales are isolated (the skill runs alone) and predictable (the
 *   same material every time).
 * - Policies: clearance = cumulative 3, mastery = streak 3 (seed data,
 *   decision #128.3; product-wide default pending Patrick).
 * - Packages: three content recipes (one per tier), shared across the unit's
 *   tiers, which differ only in the opening bpm.
 * - Tempo waypoints are meets-or-beats thresholds; nothing here encodes a
 *   literal goal-tempo gate (Phase 1 plan section 7 watch item).
 * - The library: Major is level 1, authored here; the other 79 playable scales
 *   are minted from the catalog's formulas with these same tiers, register and
 *   grading (scaleCatalogContent.ts), each at its permanent level number.
 */

import type { Grading, ScaleCell, ScaleName, Tonic } from 'Types/ExerciseContentTypes';
import type { RegisterSpec, StepwiseRunPrompt } from 'Types/ExerciseRecipeTypes';
import { generateScaleCatalogContent } from './scaleCatalogContent';
import type {
  SeedExerciseDump,
  SeedExerciseLevel,
  SeedExercisePackage,
  SeedExerciseTier,
  SeedTierWaypoint,
} from './seedTypes';

/* ------------------------------------------------------------------ *
 * The twelve major tonics - the cell axis the ladder scopes over.
 * ------------------------------------------------------------------ */

const ALL_TONICS: Tonic[] = ['C', 'G', 'D', 'A', 'E', 'B', 'F#', 'Db', 'Ab', 'Eb', 'Bb', 'F'];

/* ------------------------------------------------------------------ *
 * Tier waypoints - one templated triple, reused by every level.
 * ------------------------------------------------------------------ */

const WAYPOINTS: Record<1 | 2 | 3, SeedTierWaypoint> = {
  1: {
    hand: 'HT',
    range_octaves: 1,
    direction: 'asc',
    rhythm_density: 'quarter',
    tempo: 60,
    order_mode: 'fixed',
  },
  2: {
    hand: 'HT',
    range_octaves: 2,
    direction: 'both',
    rhythm_density: 'eighth',
    tempo: 92,
    order_mode: 'fixed',
  },
  3: {
    hand: 'HT',
    range_octaves: 4,
    direction: 'both',
    rhythm_density: 'sixteenth',
    tempo: 120,
    order_mode: 'shuffled',
  },
};

/** Opening tempo per tier: the waypoint tempo (the in-session ramp floats from there). */
const OPENING_BPM: Record<1 | 2 | 3, number> = { 1: 60, 2: 92, 3: 120 };

/* ------------------------------------------------------------------ *
 * Packages - three content recipes: {B, I, A}.
 * ------------------------------------------------------------------ */

const MAJOR_SCALE_INTERVALS = [2, 2, 1, 2, 2, 2, 1];

const REGISTER: RegisterSpec = {
  // Anchor the run near the keyboard's center as it widens: base near C4 for
  // 1-2 octaves, near C3 for 3-4. The RH starts on the tonic instance nearest
  // the anchor (ties resolve downward); the LH always sits an octave below
  // that base, even when playing alone.
  rh_anchor_by_range: { 1: 'C4', 2: 'C4', 3: 'C3', 4: 'C3' },
  lh_offset_octaves: -1,
};

/**
 * The run's prompt, shared by all three packages. The meter is a layout tool: one
 * 8/4 bar holds a one-octave quarter-note scale (eighths and sixteenths fill it two
 * and four times), and it is not drawn. The tempos stay quarter = 60 / 92 / 120.
 */
export const SCALE_RUN_PROMPT: StepwiseRunPrompt = {
  figure: 'stepwise_run',
  intervals: MAJOR_SCALE_INTERVALS,
  time_signature: [8, 4],
  show_time_signature: false,
  register: REGISTER,
  traversal: { kind: 'single_cell' },
};

const GRADING: Grading = 'timing_standard';

const packageFor = (tier: 1 | 2 | 3): SeedExercisePackage => ({
  key: `stepwise-run-content-t${tier}`,
  prompt: SCALE_RUN_PROMPT,
  interaction_family: 'performed',
  scene_config: null,
  grading: GRADING,
  defaults: { bpm: OPENING_BPM[tier] },
});

const PACKAGES: SeedExercisePackage[] = [packageFor(1), packageFor(2), packageFor(3)];

/* ------------------------------------------------------------------ *
 * The scale unit - one scale/mode holding its twelve tonic-cells.
 * ------------------------------------------------------------------ */

const scope = (tonics: Tonic[]): ScaleCell[] =>
  tonics.map((tonic) => ({ scale: 'major' satisfies ScaleName, tonic }));

const tiers = (): SeedExerciseTier[] =>
  ([1, 2, 3] as const).map((tier) => ({
    tier,
    waypoint_config: WAYPOINTS[tier],
    package_key: `stepwise-run-content-t${tier}`,
  }));

const MAJOR_LEVEL: SeedExerciseLevel = {
  level: 1,
  title: 'Major',
  cell_scope: scope(ALL_TONICS),
  is_review: false,
  description: 'The major scale in all twelve keys.',
  tiers: tiers(),
};

/* ------------------------------------------------------------------ *
 * The rest of the scale library - one level per playable unit, minted
 * from the catalog's formulas with this exercise's tiers and register.
 * ------------------------------------------------------------------ */

const CATALOG = generateScaleCatalogContent({
  waypoints: WAYPOINTS,
  openingBpm: OPENING_BPM,
  register: REGISTER,
  grading: GRADING,
});

const LEVELS: SeedExerciseLevel[] = [MAJOR_LEVEL, ...CATALOG.levels];

/* ------------------------------------------------------------------ *
 * The exercise.
 * ------------------------------------------------------------------ */

export const scalesPerformanceRead: SeedExerciseDump = {
  exercise: {
    slug: 'scales-performance-read',
    title: 'Play Scales from Notation',
    exercise_type: 'scale_read',
    content_class: 'BL-020',
    topic: 'scales_keys',
    approach: 'by_playing',
    session_pattern: 'SP-03',
    bundling: 'isolated',
    predictability: 'predictable',
    clearance_policy: { type: 'cumulative', n: 3 },
    mastery_policy: { type: 'streak', n: 3 },
    levels: LEVELS,
  },
  packages: [...PACKAGES, ...CATALOG.packages],
};
